import { useQuery } from '@tanstack/react-query';
import { BookOpen, CornerDownLeft, LayoutTemplate, Search, Server as ServerIcon } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { navigationFor } from '@/components/layout/navigation';
import { useAuth } from '@/hooks/use-auth';
import { AUTOMATION_CATALOG_KEY, automationApi } from '@/lib/automation-api';
import { serversApi } from '@/lib/servers-api';

interface Hit {
  id: string;
  label: string;
  hint: string;
  to: string;
  icon: typeof Search;
}

/**
 * The search behind the box in the header.
 *
 * ## It used to be a picture of a search box
 *
 * A `<button>` with a magnifying glass, the word "Search…", and a ⌘K badge — and no handler. It
 * looked exactly like the real thing, advertised a shortcut that did nothing, and sat at the top of
 * every page in the product. A control that cannot work is worse than an absent one: people try it,
 * conclude the product is broken, and stop trusting the parts that do work.
 *
 * ## What it searches
 *
 * Pages first, then the customer's own servers, then the playbooks and templates they can run. Pages
 * are the common case — most uses of a palette like this are "take me to Backups" — and they cost
 * nothing, being already in memory and already filtered to what this role may see.
 *
 * Servers and the automation catalogue are fetched only once the palette is open, so the header
 * costs no requests on a page nobody searches from.
 */
export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const navigate = useNavigate();
  const { user } = useAuth();
  const inputRef = useRef<HTMLInputElement>(null);

  const servers = useQuery({
    queryKey: ['servers'],
    queryFn: () => serversApi.list(),
    enabled: open,
    staleTime: 60_000,
  });

  const catalog = useQuery({
    queryKey: AUTOMATION_CATALOG_KEY,
    queryFn: () => automationApi.catalog(),
    enabled: open,
    staleTime: 300_000,
  });

  // Only the destinations this role can actually reach. Offering an operator-only page to a
  // customer would be offering a 403.
  const pages = useMemo<Hit[]>(
    () =>
      navigationFor(user?.role).flatMap((section) =>
        section.items
          .filter((item) => item.enabled)
          .map((item) => ({
            id: `page:${item.to}`,
            label: item.label,
            hint: section.title,
            to: item.to,
            icon: item.icon,
          })),
      ),
    [user?.role],
  );

  const hits = useMemo<Hit[]>(() => {
    const needle = query.trim().toLowerCase();

    const all: Hit[] = [
      ...pages,
      ...(servers.data?.data ?? []).map((server) => ({
        id: `server:${server.id}`,
        label: server.name,
        hint: server.ssh_host === server.name ? 'Server' : `Server · ${server.ssh_host}`,
        to: `/servers/${server.id}`,
        icon: ServerIcon,
      })),
      ...(catalog.data?.playbooks ?? []).map((playbook) => ({
        id: `playbook:${playbook.key}`,
        label: playbook.name,
        hint: 'Playbook',
        to: `/automation/playbooks/${playbook.key}`,
        icon: BookOpen,
      })),
      ...(catalog.data?.templates ?? []).map((template) => ({
        id: `template:${template.key}`,
        label: template.name,
        hint: 'Template',
        to: `/automation/templates/${template.key}`,
        icon: LayoutTemplate,
      })),
    ];

    if (needle === '') {
      return all.slice(0, 8);
    }

    return all
      .filter(
        (hit) =>
          hit.label.toLowerCase().includes(needle) || hit.hint.toLowerCase().includes(needle),
      )
      .slice(0, 12);
  }, [query, pages, servers.data, catalog.data]);

  useEffect(() => {
    if (open) {
      setQuery('');
      setCursor(0);
      // A palette that opens without focus is a palette you have to click before typing.
      window.setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  useEffect(() => {
    setCursor(0);
  }, [query]);

  if (!open) {
    return null;
  }

  const go = (hit: Hit | undefined) => {
    if (hit === undefined) {
      return;
    }

    onClose();
    navigate(hit.to);
  };

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setCursor((c) => Math.min(c + 1, Math.max(hits.length - 1, 0)));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setCursor((c) => Math.max(c - 1, 0));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      go(hits[cursor]);
    } else if (event.key === 'Escape') {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 pt-[12vh]"
      onClick={onClose}
      role="presentation"
    >
      <div
        className="w-full max-w-xl overflow-hidden rounded-lg border bg-background shadow-2xl"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Search"
      >
        <div className="flex items-center gap-3 border-b px-4">
          <Search className="size-4 shrink-0 text-muted-foreground" />
          <input
            ref={inputRef}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onKeyDown}
            className="h-12 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            aria-label="Search pages, servers, playbooks and templates"
          />
          <kbd className="rounded border px-1.5 py-0.5 font-mono text-[10px] text-muted-foreground">
            esc
          </kbd>
        </div>

        <ul className="max-h-80 overflow-y-auto p-2">
          {hits.length === 0 && (
            <li className="px-3 py-6 text-center text-sm text-muted-foreground">
              Nothing matches “{query}”.
            </li>
          )}

          {hits.map((hit, index) => (
            <li key={hit.id}>
              <button
                type="button"
                onClick={() => go(hit)}
                onMouseEnter={() => setCursor(index)}
                className={`flex w-full items-center gap-3 rounded-md px-3 py-2 text-left text-sm transition-colors ${
                  index === cursor ? 'bg-primary text-primary-foreground' : 'hover:bg-muted/60'
                }`}
              >
                <hit.icon className="size-4 shrink-0 opacity-70" />
                <span className="truncate font-medium">{hit.label}</span>
                <span
                  className={`ml-auto truncate text-xs ${
                    index === cursor ? 'opacity-80' : 'text-muted-foreground'
                  }`}
                >
                  {hit.hint}
                </span>
                {index === cursor && <CornerDownLeft className="size-3 shrink-0 opacity-70" />}
              </button>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
